import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaClock, FaEnvelope, FaSatelliteDish } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ROLES_VEN_RADAR, puedeActuarRadar } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  cargarDatosAlertasRecurrencia,
  calcularAlertasRecurrencia,
  formatMontoCompacto,
  motivoAlerta,
  textoEstadoRadar,
} from "../../utils/alertasRecurrencia";
import { postergarAlerta } from "../../utils/alertasRecurrenciaDescarte";
import { getEdificiosOperativos } from "../../utils/getEdificiosOperativos";
import { fetchContactosPorLado } from "../../utils/radarContactos";
import { useCurrency } from "../../utils/useCurrency";
import { EscribirRadarModal } from "./EscribirRadarModal";
import styles from "./stylesAlertasRecurrencia.module.scss";

// "Radar Comercial" del Home (/admin) — docs/modulos/alertas-recurrencia-comercial.md. Entra en la
// misma familia que Proyección Comercial y Comisión Comercial (Ignacio, 2026-09-25) — el nombre
// completo deja claro qué barre. El ámbar es deliberado: el rojo queda reservado para los
// contadores de deuda real (Facturación, Caja); un cliente para retomar es una oportunidad, no un
// error. Vive DENTRO del <Header> vía `centerContent`, AL MEDIO (Admin/index.js arma el contenedor
// que lo centra mientras <CumpleanosBanner /> se queda pegado a la derecha).
//
// EL PANEL SE RENDERIZA EN UN PORTAL A document.body (fix 2026-09-25, ver ficha "motivo real del
// bug de apilado"): `.headerCenterZoneCentro` (stylesAdmin.module.scss) tiene `transform:
// translateX(-50%)` para centrar el botón — y CUALQUIER `transform` crea un nuevo stacking context
// Y ADEMÁS convierte a ese elemento en el containing block de sus descendientes `position:fixed`
// (spec CSS, no un detalle de este componente). Por eso NI subir el z-index NI cambiar a
// `position:fixed` sin portal alcanzaba: el panel seguía atrapado dentro de esa caja transformada,
// compitiendo solo contra sus hermanos ahí adentro, nunca contra el resto de la página. El portal
// saca el nodo del DOM entero a un hijo directo de `<body>`, fuera del subárbol transformado — ahí
// `position:fixed` sí resuelve contra el viewport real. Coordenadas medidas con
// `getBoundingClientRect()` sobre el botón disparador, recalculadas en resize/scroll mientras está
// abierto (ver `actualizarCoords`/el efecto de listeners).
//
// SIN DESCARTE PERMANENTE (Fase 3 del rediseño, 2026-09-26): la cruz que mataba un aviso "hasta el
// año que viene" se retiró. La única acción por fila es "Postergar" (`FaClock`, mismo peso visual
// que la cruz de antes): un click oculta ESE aviso 7 días y vuelve solo — nunca lo mata para
// siempre. Ningún botón manual apaga un aviso de forma definitiva; solo lo apagan hechos reales
// (cierre, propuesta abierta, actividad de CRM) o una postergación que todavía no venció.
export const AlertasRecurrenciaBanner = () => {
  const { pais, id, rol, nombre, apellido, correo } = useAuthContext();
  const puedeVer = ROLES_VEN_RADAR.includes(rol);
  const puedeActuar = puedeActuarRadar(rol, id);
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState([]);
  const [postergarDisponible, setPostergarDisponible] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState(null); // {top, left} en coordenadas de viewport (fixed)
  const triggerRef = useRef(null); // el botón "Radar Comercial" — mide posición + outside-click
  const panelRef = useRef(null); // el panel portalado — outside-click
  // Fase B ("Escribir"): contactos por lado (clientId de la venta de referencia de cada alerta
  // VISIBLE, batcheado — nunca una query por fila), M de hoy (edificios operativos, fuente única
  // `getEdificiosOperativos`) y el modal abierto (o null).
  const [contactosPorCliente, setContactosPorCliente] = useState(new Map());
  const [edificiosOperativos, setEdificiosOperativos] = useState(null);
  const [modalRadar, setModalRadar] = useState(null); // { alerta, lado } | null

  useEffect(() => {
    // Gate ANTES de cargarDatosAlertasRecurrencia (no solo en el render): un rol fuera de
    // ROLES_VEN_RADAR nunca dispara la query, aunque el componente llegue a montarse.
    if (!pais || pais === "Region" || !puedeVer) return undefined;
    let vivo = true;
    cargarDatosAlertasRecurrencia({ pais })
      .then(({ oportunidades, clientes, descartes, mailSalienteResumen }) => {
        if (!vivo) return;
        setPostergarDisponible(descartes !== null); // null = posponer_hasta todavía no existe (10.137)
        const todas = calcularAlertasRecurrencia({
          oportunidades,
          clientes,
          descartes,
          mailSalienteResumen,
          pais,
        });
        // El header y el panel muestran SOLO pendiente + vencida (Fase A, 2026-09-26) — cerrada,
        // escrita y postergada quedan calculadas pero fuera del panel (van a la página de gestión
        // de una fase futura).
        const visibles = todas.filter((a) => a.estado === "pendiente" || a.estado === "vencida");
        setAlertas(visibles);

        // Fase B: contactos por lado de los `clientId` de referencia de las alertas VISIBLES
        // (nunca de las ~400 empresas del país) + M de hoy. Ninguno de los dos bloquea el panel
        // si falla — sin contactos, no se muestran sobres; sin M, la frase de crecimiento se omite
        // (ver plantillasRadar.js).
        const clientIds = visibles.map((a) => a.clientId).filter((cid) => cid != null);
        fetchContactosPorLado({ clientIds })
          .then((mapa) => vivo && setContactosPorCliente(mapa))
          .catch(() => {});
        getEdificiosOperativos({ pais })
          .then((edificios) => vivo && setEdificiosOperativos(edificios.length))
          .catch(() => {});
      })
      .catch(() => {
        // Sin alertas visibles ante error de carga — nunca rompe el Home (10.177).
        if (vivo) setAlertas([]);
      });
    return () => {
      vivo = false;
    };
  }, [pais, puedeVer]);

  const PANEL_WIDTH = 1000;
  const MARGEN = 8;

  const actualizarCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const anchoDisponible = Math.min(PANEL_WIDTH, window.innerWidth - MARGEN * 2);
    let left = rect.left + rect.width / 2 - anchoDisponible / 2;
    left = Math.max(MARGEN, Math.min(left, window.innerWidth - anchoDisponible - MARGEN));
    setCoords({ top: rect.bottom + 6, left, width: anchoDisponible });
  }, []);

  // Mide ANTES del paint (useLayoutEffect) para que el panel no aparezca un frame en (0,0).
  useLayoutEffect(() => {
    if (abierto) actualizarCoords();
  }, [abierto, actualizarCoords]);

  // Reposiciona mientras está abierto: la ventana puede cambiar de tamaño o la página puede
  // scrollear (el botón se mueve en pantalla aunque su posición en el documento no cambie).
  // `scroll` con capture:true agarra el scroll de CUALQUIER contenedor anidado, no solo window.
  useEffect(() => {
    if (!abierto) return undefined;
    window.addEventListener("resize", actualizarCoords);
    window.addEventListener("scroll", actualizarCoords, true);
    return () => {
      window.removeEventListener("resize", actualizarCoords);
      window.removeEventListener("scroll", actualizarCoords, true);
    };
  }, [abierto, actualizarCoords]);

  // Cierre al click afuera: ahora hay que chequear DOS nodos (el botón y el panel portalado ya no
  // son el mismo subárbol del DOM desde que el panel vive en document.body).
  useEffect(() => {
    if (!abierto) return undefined;
    const handler = (e) => {
      const fueraDelBoton = triggerRef.current && !triggerRef.current.contains(e.target);
      const fueraDelPanel = panelRef.current && !panelRef.current.contains(e.target);
      if (fueraDelBoton && fueraDelPanel) setAbierto(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [abierto]);

  // Redundante con el gate del efecto de arriba (alertas nunca se puebla si !puedeVer) — explícito
  // acá también para que el botón/contador/panel nunca dependan solo de que el fetch haya sido
  // gateado correctamente (defensa en profundidad de un gate de permisos).
  if (!puedeVer || alertas.length === 0) return null;

  const irAEmpresa = (alerta) => {
    setAbierto(false);
    navigate("/admin/ventas/crm", { state: { filtroEmpresaAlerta: alerta.empresaDisplay } });
  };

  const postergar = async (e, alerta) => {
    e.stopPropagation();
    const clave = `${alerta.empresaNorm}|${alerta.mesObjetivo}|${alerta.anoObjetivo}`;
    setAlertas((prev) => prev.filter((a) => `${a.empresaNorm}|${a.mesObjetivo}|${a.anoObjetivo}` !== clave));
    try {
      await postergarAlerta({
        pais,
        empresaNorm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        usuarioId: id,
      });
    } catch {
      // No se pudo guardar la postergación — la alerta ya se sacó de la vista de esta sesión
      // igual; si recarga la página, vuelve a aparecer (10.177: no se finge una postergación que no
      // se guardó).
    }
  };

  const montoTotal = alertas.reduce((suma, a) => suma + a.monto, 0);

  const panel = abierto && coords && (
    <div className={styles.panel} ref={panelRef} style={{ top: coords.top, left: coords.left, width: coords.width }}>
      <div className={styles.encabezado}>
        <div className={styles.encabezadoTitulo}>
          Encontramos {alertas.length} {alertas.length === 1 ? "cliente" : "clientes"}.{" "}
          {formatMontoCompacto(montoTotal)} entre todos.
        </div>
      </div>
      <div className={styles.lista}>
        {alertas.map((alerta) => {
          const clave = `${alerta.empresaNorm}-${alerta.mesObjetivo}-${alerta.anoObjetivo}`;
          const contactos = contactosPorCliente.get(alerta.clientId) || { cliente: [], agencia: [] };
          const hayCliente = contactos.cliente.length > 0;
          const hayAgencia = contactos.agencia.length > 0;
          return (
            <div key={clave} className={styles.fila} onClick={() => irAEmpresa(alerta)}>
              <span className={`${styles.punto} ${alerta.tipo === "compro" ? styles.puntoVerde : styles.puntoGris}`} />
              <span className={styles.colEmpresa} title={alerta.empresaDisplay}>
                {alerta.empresaDisplay}
              </span>
              <span className={styles.colMotivo} title={motivoAlerta(alerta)}>
                {motivoAlerta(alerta)}
              </span>
              <span className={styles.colMonto}>{formatAmount(alerta.monto)}</span>
              {/* Columna de acciones — ancho fijo (95px, grid-template-columns de `.fila`), igual
                  para quien actúa y quien solo ve (`puedeActuarRadar`, permisos.js): el importe
                  nunca se mueve. Quien actúa ve 3 lugares reservados (sobre C, sobre A, reloj),
                  vacíos pero ocupando su ancho si esa acción no aplica en esta fila, con 4 barras
                  finas — una antes de cada acción y una al cierre. Quien solo ve, el estado en
                  texto (`textoEstadoRadar`) centrado en el mismo slot. */}
              <span className={styles.acciones}>
                {!puedeActuar ? (
                  <span className={styles.estadoTexto} title={textoEstadoRadar(alerta)}>
                    {textoEstadoRadar(alerta)}
                  </span>
                ) : (
                  <>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>
                      {hayCliente && (
                        <button
                          type="button"
                          className={styles.accionBoton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalRadar({ alerta, lado: "cliente" });
                          }}
                          title="Escribir a Cliente"
                        >
                          <FaEnvelope />
                          <span className={styles.sobreLetra}>C</span>
                        </button>
                      )}
                    </span>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>
                      {hayAgencia && (
                        <button
                          type="button"
                          className={styles.accionBoton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalRadar({ alerta, lado: "agencia" });
                          }}
                          title="Escribir a Agencia"
                        >
                          <FaEnvelope />
                          <span className={styles.sobreLetra}>A</span>
                        </button>
                      )}
                    </span>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>
                      {postergarDisponible && (
                        <button
                          type="button"
                          className={styles.accionBoton}
                          onClick={(e) => postergar(e, alerta)}
                          title="Postergar 7 días"
                        >
                          <FaClock />
                        </button>
                      )}
                    </span>
                    <span className={styles.divisorAcciones} />
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className={styles.pie}>
        <span className={styles.pieLlave}>
          <span className={`${styles.punto} ${styles.puntoVerde}`} /> Compró
        </span>
        <span className={styles.pieLlave}>
          <span className={`${styles.punto} ${styles.puntoGris}`} /> Se propuso
        </span>
        <span className={styles.pieDescarte}>Postergar lo oculta 7 días y vuelve solo</span>
      </div>
    </div>
  );

  return (
    <div className={styles.contenedor} ref={triggerRef}>
      <button type="button" className={styles.boton} onClick={() => setAbierto((v) => !v)}>
        <FaSatelliteDish className={styles.icono} aria-label="radar" />
        <span className={styles.label}>Radar Comercial</span>
        <span className={styles.badge}>{alertas.length}</span>
      </button>
      {panel && createPortal(panel, document.body)}
      {modalRadar &&
        (() => {
          const contactosCliente = contactosPorCliente.get(modalRadar.alerta.clientId) || {
            cliente: [],
            agencia: [],
          };
          return (
            <EscribirRadarModal
              isOpen
              onClose={() => setModalRadar(null)}
              lado={modalRadar.lado}
              alerta={modalRadar.alerta}
              contactos={contactosCliente[modalRadar.lado]}
              edificiosOperativos={edificiosOperativos}
              pais={pais}
              usuarioId={id}
              usuarioNombre={`${nombre || ""} ${apellido || ""}`.trim()}
              usuarioEmail={correo}
            />
          );
        })()}
    </div>
  );
};
